import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDown, X } from 'lucide-react';
import { textIncludesQuery } from '@narrativebench/editor-core';
import { Popover, PopoverContent, PopoverTrigger } from './popover';
import { cn } from '@/lib/utils';

export interface SingleSelectComboboxOption {
  value: string;
  label: string;
}

export interface SingleSelectComboboxSection {
  label: string;
  options: SingleSelectComboboxOption[];
}

interface SingleSelectComboboxProps {
  options?: SingleSelectComboboxOption[];
  sections?: SingleSelectComboboxSection[];
  value: string;
  onValueChange: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  className?: string;
  align?: 'start' | 'center' | 'end';
  inputClassName?: string;
  contentClassName?: string;
  disabled?: boolean;
  clearable?: boolean;
  onClear?: () => void;
  clearAriaLabel?: string;
}

function optionMatchesSearch(option: SingleSelectComboboxOption, search: string): boolean {
  return textIncludesQuery(option.value, search) || textIncludesQuery(option.label, search);
}

export const SingleSelectCombobox: React.FC<SingleSelectComboboxProps> = ({
  options = [],
  sections,
  value,
  onValueChange,
  placeholder,
  searchPlaceholder,
  emptyMessage = 'No results found.',
  className,
  align = 'start',
  inputClassName,
  contentClassName,
  disabled = false,
  clearable = false,
  onClear,
  clearAriaLabel = 'Clear',
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const flatOptions = useMemo(() => {
    if (sections && sections.length > 0) {
      return sections.flatMap((section) => section.options);
    }
    return options;
  }, [options, sections]);

  const filteredSections = useMemo((): SingleSelectComboboxSection[] | null => {
    if (!sections || sections.length === 0) {
      return null;
    }
    const query = search.trim();
    return sections
      .map((section) => ({
        label: section.label,
        options: query
          ? section.options.filter((option) => optionMatchesSearch(option, query))
          : section.options,
      }))
      .filter((section) => section.options.length > 0);
  }, [sections, search]);

  const filteredOptions = useMemo(() => {
    if (filteredSections) {
      return filteredSections.flatMap((section) => section.options);
    }
    if (!search.trim()) {
      return options;
    }
    return options.filter((option) => optionMatchesSearch(option, search));
  }, [filteredSections, options, search]);

  const selectedLabel = flatOptions.find((option) => option.value === value)?.label ?? value;

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    setTimeout(() => inputRef.current?.focus(), 0);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    let removeWheelListener: (() => void) | undefined;
    const frame = window.requestAnimationFrame(() => {
      const list = listRef.current;
      if (!list) {
        return;
      }
      const onWheel = (event: WheelEvent): void => {
        event.stopPropagation();
        list.scrollTop += event.deltaY;
        event.preventDefault();
      };
      list.addEventListener('wheel', onWheel, { passive: false });
      removeWheelListener = () => {
        list.removeEventListener('wheel', onWheel);
      };
    });
    return () => {
      window.cancelAnimationFrame(frame);
      removeWheelListener?.();
    };
  }, [open, filteredOptions.length]);

  const displayValue = open ? search : selectedLabel;

  const renderOptionButton = (opt: SingleSelectComboboxOption): React.ReactElement => (
    <button
      key={opt.value}
      type="button"
      className={cn(
        'flex items-center w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent',
        opt.value === value && 'bg-accent'
      )}
      onClick={() => {
        onValueChange(opt.value);
        setOpen(false);
      }}
    >
      {opt.label}
    </button>
  );

  return (
    <Popover
      modal={false}
      open={disabled ? false : open}
      onOpenChange={(next) => {
        if (!disabled) setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <div
          className={cn(
            'flex h-9 w-full min-w-0 items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background focus-within:outline-none focus-within:ring-1 focus-within:ring-ring',
            disabled && 'cursor-not-allowed opacity-50',
            inputClassName
          )}
        >
          <input
            ref={inputRef}
            value={displayValue}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => {
              if (!disabled) setOpen(true);
            }}
            placeholder={open ? (searchPlaceholder ?? placeholder) : placeholder}
            readOnly={!open || disabled}
            disabled={disabled}
            className="min-w-0 flex-1 truncate bg-transparent outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed"
          />
          {clearable && !disabled && onClear ? (
            <button
              type="button"
              aria-label={clearAriaLabel}
              className="rounded-full p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                setOpen(false);
                onClear();
              }}
              onPointerDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
          <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
        </div>
      </PopoverTrigger>
      <PopoverContent
        className={cn('w-[var(--radix-popover-trigger-width)] p-0', className, contentClassName)}
        align={align}
      >
        <div
          ref={listRef}
          className="max-h-48 overflow-y-auto overscroll-contain p-1"
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          {filteredOptions.length === 0 ? (
            <div className="py-6 text-center text-sm text-muted-foreground">{emptyMessage}</div>
          ) : filteredSections ? (
            <div className="flex flex-col gap-2">
              {filteredSections.map((section) => (
                <div key={section.label} className="flex flex-col gap-0.5">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {section.label}
                  </div>
                  {section.options.map(renderOptionButton)}
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col gap-0.5">
              {filteredOptions.map(renderOptionButton)}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
